import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { formatDateTime } from "@/utils/dateUtils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  User,
  Building,
  MapPin,
  Wrench,
  Plus,
  Clock,
  Mail,
  AlertCircle,
  Trash2,
  Palette,
  Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button"; // New import for Button
import { PermissionGuard } from "@/components/auth/RoleGuard";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { showSuccess, showError, showWarning } from "@/utils/toastHelpers";

import PracticeInfoForm from "../components/settings/ClinicProfileForm";
import RoomManagement from "../components/settings/RoomManagement";
import EquipmentManagement from "../components/settings/EquipmentManagement";
import ProviderAvailabilityForm from "../components/settings/ProviderAvailabilityForm";
import AddDoctorModal from "../components/settings/AddDoctorModal";
import ZoomConnectionCard from "../components/settings/ZoomConnectionCard";
import PayfastMerchantSetup from "../components/payfast/PayfastMerchantSetup";

export default function Settings() {
  const [showProviderAvailability, setShowProviderAvailability] =
    useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showAddDoctor, setShowAddDoctor] = useState(false);
  const [editColorDoctor, setEditColorDoctor] = useState(null);
  const [selectedColor, setSelectedColor] = useState("#3b82f6");

  // Fetch doctors using React Query
  const {
    data: doctors = [],
    isLoading: isDoctorsLoading,
    error: doctorsError,
  } = useQuery({
    queryKey: ["admin-doctors"], // Use unique key for admin endpoint
    queryFn: () => apiClient.get("/api/users/doctors"),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const queryClient = useQueryClient();

  // Mutation for saving doctor availability
  const saveAvailabilityMutation = useMutation({
    mutationFn: ({ doctorId, availabilityData }) =>
      apiClient.put(
        `/api/users/doctors/${doctorId}/availability`,
        availabilityData
      ),
    onSuccess: () => {
      setShowProviderAvailability(false);
      setSelectedProvider(null);
      // Refresh doctor availability data
      queryClient.invalidateQueries(["admin-doctors"]);
      queryClient.invalidateQueries(["doctorAvailability"]);
      // Invalidate available dates since provider availability affects slot availability
      queryClient.invalidateQueries(["bookings", "available-dates"]);
      queryClient.invalidateQueries(["bookings", "slots"]);
    },
    onError: (error) => {
      console.error("Error saving provider availability:", error);
    },
  });

  // Mutation for inviting doctors
  const inviteDoctorMutation = useMutation({
    mutationFn: (doctorId) => apiClient.post(`/api/doctors/${doctorId}/invite`),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-doctors"]);
      showSuccess("Invitation sent successfully!");
    },
    onError: (error) => {
      console.error("Error sending doctor invitation:", error);
      const errorMessage =
        error?.data?.error || "Failed to send invitation. Please try again.";
      showError("Failed to send invitation", errorMessage);
    },
  });

  // Mutation for removing doctors
  const removeDoctorMutation = useMutation({
    mutationFn: (doctorId) =>
      apiClient.delete(`/api/users/doctors/${doctorId}`),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["admin-doctors"]);
      const doctorName = data?.doctorName || "Doctor";
      showSuccess(`${doctorName} has been removed from the practice successfully`);
    },
    onError: (error) => {
      console.error("Error removing doctor:", error);
      const errorMessage =
        error?.data?.error || "Failed to remove doctor. Please try again.";
      showError("Failed to remove doctor", errorMessage);
    },
  });

  // Mutation for updating doctor color
  const updateDoctorColorMutation = useMutation({
    mutationFn: ({ doctorId, color }) =>
      apiClient.patch(`/api/users/doctors/${doctorId}`, { color }),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-doctors"]);
      queryClient.invalidateQueries(["doctors"]);
      setEditColorDoctor(null);
      showSuccess("Doctor color updated successfully!");
    },
    onError: (error) => {
      console.error("Error updating doctor color:", error);
      const errorMessage =
        error?.data?.error || "Failed to update color. Please try again.";
      showError("Failed to update color", errorMessage);
    },
  });

  // Handle adding a new doctor
  const handleAddDoctor = () => {
    setShowAddDoctor(true);
  };

  // Handle successful doctor creation
  const handleDoctorCreated = () => {
    setShowAddDoctor(false);
    // The React Query will automatically refetch doctors
  };

  // Handle inviting a doctor
  const handleInviteDoctor = (doctorId) => {
    inviteDoctorMutation.mutate(doctorId);
  };

  // Handle removing a doctor
  const handleRemoveDoctor = (doctor) => {
    const confirmMessage = `Are you sure you want to remove ${doctor.name} from the practice? This action cannot be undone.`;
    if (window.confirm(confirmMessage)) {
      removeDoctorMutation.mutate(doctor.id);
    }
  };

  // Handle saving provider availability
  const handleSaveProviderAvailability = async (providerData) => {
    try {
      if (!selectedProvider?.id) {
        console.error("No doctor selected");
        return;
      }

      // Transform the form data to match backend API expectations
      const availabilityData = {
        operating_hours: providerData.operating_hours,
      };

      saveAvailabilityMutation.mutate({
        doctorId: selectedProvider.id,
        availabilityData,
      });
    } catch (error) {
      console.error("Error saving provider availability:", error);
    }
  };

  // Handle opening color edit dialog
  const handleEditColor = (doctor) => {
    setEditColorDoctor(doctor);
    setSelectedColor(doctor.color || "#3b82f6");
  };

  // Handle saving color
  const handleSaveColor = () => {
    if (!editColorDoctor) return;

    // Validate hex color format
    const hexColorRegex = /^#[0-9A-F]{6}$/i;
    if (!hexColorRegex.test(selectedColor)) {
      showWarning("Please enter a valid hex color (e.g., #3b82f6)");
      return;
    }

    updateDoctorColorMutation.mutate({
      doctorId: editColorDoctor.id,
      color: selectedColor,
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Settings</h1>
          <p className="text-slate-600">
            Manage your practice information, rooms, equipment, and provider
            availability settings.
          </p>
        </div>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-5 bg-slate-100/80 h-auto">
          <TabsTrigger value="profile" className="gap-2 py-2">
            <Building className="w-4 h-4" />
            Practice Info
          </TabsTrigger>
          <TabsTrigger value="rooms" className="gap-2 py-2" data-testid="settings-rooms-tab">
            <MapPin className="w-4 h-4" />
            Rooms
          </TabsTrigger>
          <TabsTrigger value="equipment" className="gap-2 py-2" data-testid="settings-equipment-tab">
            <Wrench className="w-4 h-4" />
            Equipment
          </TabsTrigger>
          <TabsTrigger value="providers" className="gap-2 py-2" data-testid="settings-providers-tab">
            <User className="w-4 h-4" />
            Providers
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2 py-2" data-testid="settings-integrations-tab">
            <Link2 className="w-4 h-4" />
            Integrations
          </TabsTrigger>
        </TabsList>
        <TabsContent value="profile" className="mt-6">
          <PracticeInfoForm />
        </TabsContent>
        <TabsContent value="rooms" className="mt-6">
          <RoomManagement />
        </TabsContent>
        <TabsContent value="equipment" className="mt-6">
          <EquipmentManagement />
        </TabsContent>

        {/* New TabsContent for providers */}
        <TabsContent value="providers" className="mt-6">
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-2">
              <div>
                <h3 className="text-lg font-semibold">
                  Provider Availability Settings
                </h3>
                <p className="text-slate-600">
                  Configure provider schedules and availability.
                </p>
              </div>
              <PermissionGuard permissions={["manage_users"]}>
                <Button
                  onClick={handleAddDoctor}
                  className="gap-2 w-full sm:w-auto"
                  data-testid="add-doctor-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Doctor
                </Button>
              </PermissionGuard>
            </div>

            <div className="grid gap-4">
              {isDoctorsLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="flex items-center justify-between p-4 bg-white/60 backdrop-blur-sm border border-slate-200/60 rounded-lg shadow-sm">
                        <div className="space-y-2">
                          <div className="h-4 bg-slate-200 rounded w-32"></div>
                          <div className="h-3 bg-slate-200 rounded w-48"></div>
                        </div>
                        <div className="h-8 bg-slate-200 rounded w-24"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : doctorsError ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600">
                    Failed to load doctors: {doctorsError.message}
                  </p>
                </div>
              ) : doctors.length === 0 ? (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-center">
                  <p className="text-slate-600">
                    No doctors found in your practice.
                  </p>
                </div>
              ) : (
                <>
                  {/* Active Doctors */}
                  {doctors
                    .filter((doctor) => doctor.status === "active")
                    .map((doctor) => (
                      <div
                        key={doctor.id}
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white/60 backdrop-blur-sm border border-slate-200/60 rounded-lg shadow-sm"
                      >
                        <div className="flex items-center gap-3">
                          {/* Doctor Color Indicator */}
                          <div
                            className="w-4 h-4 rounded-full border-2 border-white shadow-sm flex-shrink-0"
                            style={{ backgroundColor: doctor.color || "#3b82f6" }}
                            title={`Doctor color: ${doctor.color || "#3b82f6"}`}
                          />
                          <div>
                            <h4 className="font-semibold">{doctor.name}</h4>
                            <p className="text-sm text-slate-600">
                              {doctor.specializations?.length > 0
                                ? `Specializations: ${doctor.specializations.join(", ")}`
                                : "Manage availability and booking settings"}
                            </p>
                            <p className="text-xs text-slate-500">
                              {doctor.email}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3 sm:mt-0 w-full sm:w-auto">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditColor(doctor)}
                            className="gap-2"
                          >
                            <Palette className="w-4 h-4" />
                            Color
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedProvider({
                                id: doctor.id,
                                provider_name: doctor.name,
                                email: doctor.email,
                                specializations: doctor.specializations || [],
                              });
                              setShowProviderAvailability(true);
                            }}
                            className="gap-2"
                            data-testid={`provider-configure-btn-${doctor.email.replace(/[@.]/g, '-')}`}
                          >
                            <Clock className="w-4 h-4" />
                            Configure
                          </Button>
                          <PermissionGuard permissions={["manage_users"]}>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveDoctor(doctor)}
                              disabled={removeDoctorMutation.isPending}
                              className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            >
                              <Trash2 className="w-4 h-4" />
                              {removeDoctorMutation.isPending
                                ? "Removing..."
                                : "Remove"}
                            </Button>
                          </PermissionGuard>
                        </div>
                      </div>
                    ))}

                  {/* Invited Doctors */}
                  {doctors.filter((doctor) => doctor.status === "invited")
                    .length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Doctors Pending Invitation
                        </span>
                      </div>
                      {doctors
                        .filter((doctor) => doctor.status === "invited")
                        .map((doctor) => (
                          <div
                            key={doctor.id}
                            className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              {/* Doctor Color Indicator */}
                              <div
                                className="w-4 h-4 rounded-full border-2 border-white shadow-sm flex-shrink-0"
                                style={{ backgroundColor: doctor.color || "#3b82f6" }}
                                title={`Doctor color: ${doctor.color || "#3b82f6"}`}
                              />
                              <div>
                                <h4 className="font-semibold text-amber-900">
                                  {doctor.name}
                                </h4>
                                <p className="text-sm text-amber-700">
                                  {doctor.email}
                                </p>
                                <p className="text-xs text-amber-600">
                                  {doctor.specializations?.length > 0
                                    ? `Specializations: ${doctor.specializations.join(", ")}`
                                    : "Account created but needs activation"}
                                </p>
                                {doctor.lastInvitedAt && (
                                  <p className="text-xs text-amber-600 mt-1">
                                    Invitation sent:{" "}
                                    {formatDateTime(doctor.lastInvitedAt)}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3 sm:mt-0 w-full sm:w-auto">
                              <PermissionGuard permissions={["manage_users"]}>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleInviteDoctor(doctor.id)}
                                  disabled={inviteDoctorMutation.isPending}
                                  className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                                >
                                  <Mail className="w-4 h-4" />
                                  {inviteDoctorMutation.isPending
                                    ? "Sending..."
                                    : doctor.lastInvitedAt
                                    ? "Resend Invite"
                                    : "Send Invite"}
                                </Button>
                              </PermissionGuard>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditColor(doctor)}
                                className="gap-2"
                              >
                                <Palette className="w-4 h-4" />
                                Color
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedProvider({
                                    id: doctor.id,
                                    provider_name: doctor.name,
                                    email: doctor.email,
                                    specializations:
                                      doctor.specializations || [],
                                  });
                                  setShowProviderAvailability(true);
                                }}
                                className="gap-2"
                                data-testid={`provider-configure-btn-${doctor.email.replace(/[@.]/g, '-')}`}
                              >
                                <Clock className="w-4 h-4" />
                                Configure
                              </Button>
                              <PermissionGuard permissions={["manage_users"]}>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRemoveDoctor(doctor)}
                                  disabled={removeDoctorMutation.isPending}
                                  className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  {removeDoctorMutation.isPending
                                    ? "Removing..."
                                    : "Remove"}
                                </Button>
                              </PermissionGuard>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="mt-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold">External Integrations</h3>
              <p className="text-slate-600">
                Connect external services to enhance your practice capabilities.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <ZoomConnectionCard />
              <PayfastMerchantSetup />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Provider Availability Form */}
      {showProviderAvailability && (
        <ProviderAvailabilityForm
          provider={selectedProvider}
          onSave={handleSaveProviderAvailability}
          isLoading={saveAvailabilityMutation.isPending}
          onClose={() => {
            setShowProviderAvailability(false);
            setSelectedProvider(null);
          }}
        />
      )}

      {/* Add Doctor Modal */}
      <PermissionGuard permissions={["manage_users"]}>
        {showAddDoctor && (
          <AddDoctorModal
            onClose={() => setShowAddDoctor(false)}
            onSuccess={handleDoctorCreated}
          />
        )}
      </PermissionGuard>

      {/* Edit Color Dialog */}
      <Dialog open={!!editColorDoctor} onOpenChange={(open) => !open && setEditColorDoctor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Doctor Color</DialogTitle>
            <DialogDescription>
              Choose a color for {editColorDoctor?.name}. This color will be used to identify the doctor in calendars and schedules.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="colorPicker">Color</Label>
              <div className="flex gap-2">
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="color"
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                    className="w-12 h-12 rounded border-2 border-slate-200 cursor-pointer"
                  />
                  <Input
                    id="colorPicker"
                    type="text"
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                    placeholder="#3b82f6"
                    className="flex-1 font-mono"
                    maxLength={7}
                  />
                </div>
              </div>
              <p className="text-sm text-slate-500">
                Enter a hex color code (e.g., #3b82f6) or use the color picker
              </p>
            </div>
            
            {/* Color Preview */}
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div
                  className="w-10 h-10 rounded-full border-2 border-white shadow-md"
                  style={{ backgroundColor: selectedColor }}
                />
                <span className="font-medium">{editColorDoctor?.name}</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setEditColorDoctor(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveColor}
              disabled={updateDoctorColorMutation.isPending}
            >
              {updateDoctorColorMutation.isPending ? "Saving..." : "Save Color"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
